import type { SubjectRef } from '@flux/shared';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button.js';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.js';
import { subjectLink } from '@/lib/subject-link.js';

export interface JournalDetailCellProps {
  detail: Record<string, unknown> | null;
  /** Objet de l'événement — « Ouvrir l'objet » y mène quand un écran le montre. */
  subject: SubjectRef | null;
}

/**
 * Détail JSON repliable (`02` §11 : « détail JSON repliable »). `DataTable`
 * ne permet pas d'insérer de ligne supplémentaire sous une ligne donnée (pas
 * de row model enregistré, `components/data-table/features.ts`) : le détail
 * se déplie donc dans un `Popover` ancré à la cellule elle-même plutôt que
 * dans une ligne étendue — voir le rapport de la vague.
 */
export function JournalDetailCell({ detail, subject }: JournalDetailCellProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const href = subjectLink(subject);

  if (detail === null && href === null) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <span className="flex items-center gap-2 whitespace-nowrap">
      {detail !== null ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="sm" aria-expanded={open}>
              {open ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
              Détail
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-96 max-w-[calc(100vw-2rem)]">
            <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-5 text-foreground">
              {JSON.stringify(detail, null, 2)}
            </pre>
          </PopoverContent>
        </Popover>
      ) : null}
      {href !== null ? (
        <Link
          to={href}
          className="rounded-sm text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Ouvrir l’objet
        </Link>
      ) : null}
    </span>
  );
}
